import {
  Alert,
  Autocomplete,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  type FormControlProps,
} from "@mui/material";
import { type FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useFlag } from "../../../server/optimizely";
import { useDialog } from "../../../utils/hooks/use-dialog";
import type { UUID } from "../../../utils/uuid";
import type { ConnectionCategory } from "../../moment/hooks/use-connections";
import type { ProgramsState } from "../../moment/hooks/use-programs";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { useMomentFormSurveys } from "../../survey/hooks/use-moment-form-surveys";
import type { MomentFormSurvey } from "../../survey/server/supabase-survey-service";
import { SurveyAlertWizardDialog } from "./survey-alert-wizard-dialog";

export type SurveyCompletedFormProps = FormControlProps & {
  showValidationErrors: boolean;
  disabled: boolean;
  reloadMergeTags: () => void;
  programs: ProgramsState;
  connectionCategories: ConnectionCategory[];
  restrictedSegments?: UUID[];
};

export const SurveyCompletedForm: FC<SurveyCompletedFormProps> = ({
  showValidationErrors,
  disabled,
  reloadMergeTags,
  programs,
  connectionCategories,
  restrictedSegments = [],
}) => {
  const { control, setValue, getValues } = useFormContext<MomentFormMoment>();
  const [showSurveyAlertWizard] = useFlag("cha-4341-survey-alert-wizard");
  const wizardDialog = useDialog();

  useWatch({ control, name: ["trigger_survey_id"] });
  const [triggerSurveyId] = getValues(["trigger_survey_id"]);

  const { data: surveys } = useMomentFormSurveys({
    allowedStatuses: ["active", "archived"],
    excludeConfidential: true,
  });

  const selectedSurvey: MomentFormSurvey | null =
    surveys?.find((s) => s.id === triggerSurveyId) ?? null;
  const selectableSurveys = surveys?.filter((survey) => survey.status === "active") ?? [];

  const handleSurveyChange = (_: React.SyntheticEvent, survey: MomentFormSurvey | null): void => {
    setValue("trigger_survey_id", survey?.id ?? null, { shouldDirty: true });
    if (survey) reloadMergeTags();
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Stack spacing={2}>
          <Typography variant="body2">
            Send a notification whenever a survey is completed. The notification will contain all
            answers from the respondent.
          </Typography>
          {showSurveyAlertWizard && (
            <Button
              variant="text"
              sx={{ alignSelf: "flex-start" }}
              data-analytics-id="survey-completed-notification-use-wizard"
              onClick={wizardDialog.handleOpen}
            >
              Use the Wizard (recommended)
            </Button>
          )}
          <SurveyAlertWizardDialog
            open={wizardDialog.open}
            onClose={wizardDialog.handleClose}
            programs={programs}
            channels={getValues("composeMethods") ?? []}
            connectionCategories={connectionCategories}
            restrictedSegments={restrictedSegments}
          />
          <Autocomplete<MomentFormSurvey>
            options={selectableSurveys}
            getOptionLabel={(option) => option.title ?? ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedSurvey}
            onChange={handleSurveyChange}
            disabled={disabled}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.title ?? ""}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Survey"
                error={showValidationErrors && !selectedSurvey}
                helperText={showValidationErrors && !selectedSurvey ? "Select a survey" : undefined}
                sx={{ maxWidth: 305 }}
              />
            )}
          />
          <Alert severity="info">
            Anyone completing this survey via its generic link will not trigger a notification, as
            we can’t determine which segment they belong to.
          </Alert>
        </Stack>
      </Grid>
    </Grid>
  );
};
